import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showThirdBullet as showThirdBulletHelper } from '../../components/helpers/sign-up';
import SetupComponent from '../../components/signup/setup';
import analytics from '../../lib/analytics';
import { captureAbandonedSignup } from '../../lib/helpers';
import { getInvalidFields } from '../../lib/shared-signup';
import { selectState } from '../../selectors';
import { setAbandonedSignupTracker } from '../../store/data';

import type { Dispatch } from '@avalara/types/actions';
import type { PartnerSignupInfo } from '@avalara/types/reducer-states';

export type Props = {
  containsInvalidOrMissingFields: boolean;
  continueLink: string;
  continueLinkText: string;
  isDirectToBuy: boolean;
  partnerSignup: PartnerSignupInfo;
  showThirdBullet: boolean;
};

const Setup: React.FC = () => {
  const dispatch = useDispatch<Dispatch>();

  // Selectors
  const {
    system: { activeFlow },
    sharedSignup,
    avataxSignup: { partnerSignup, selectedConnector },
    data: { marketingData, abandonedSignupTracker },
    abTesting,
  } = useSelector(selectState);

  const { signupForm } = sharedSignup;

  const isDirectToBuy = activeFlow === 'avatax-direct-buy';
  const containsInvalidOrMissingFields = Boolean(getInvalidFields(sharedSignup).length);
  const continueLink = partnerSignup ? '/buy/buy' : '/buy/connector';
  const continueLinkText = partnerSignup ? 'Continue' : 'Next';
  const showThirdBullet = showThirdBulletHelper(abTesting.group, partnerSignup);

  useEffect(() => {
    if (selectedConnector) {
      analytics.page('SIGNUP_COMPANY_INFO', { connector: selectedConnector.name });
    } else {
      analytics.page('SIGNUP_COMPANY_INFO');
    }

    // prevent multiple event listeners from firing the same form abandonment capture
    window.removeEventListener('unload', abandonedSignupTracker || (() => undefined));

    // Use the already allocated abandonment side effect, or create a new one.
    const boundAbandoned =
      abandonedSignupTracker ||
      captureAbandonedSignup.bind(null, {
        activeFlow,
        firstName: signupForm.firstName,
        lastName: signupForm.lastName,
        email: signupForm.email,
        phone: signupForm.phone,
        company: {
          address: signupForm.street1,
          company_name: signupForm.companyName,
          city: signupForm.city,
          country: signupForm.country,
          postal_code: signupForm.postalCode,
          state: signupForm.state,
        },
        marketingData,
      });

    window.addEventListener('unload', boundAbandoned);

    dispatch(setAbandonedSignupTracker(boundAbandoned));

    // Cleanup on unmount
    return () => {
      window.removeEventListener('unload', boundAbandoned);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow, selectedConnector, signupForm, marketingData, abandonedSignupTracker]);

  // Pass all props to SetupComponent
  return (
    <SetupComponent
      containsInvalidOrMissingFields={containsInvalidOrMissingFields}
      continueLink={continueLink}
      continueLinkText={continueLinkText}
      isDirectToBuy={isDirectToBuy}
      partnerSignup={partnerSignup}
      showThirdBullet={showThirdBullet}
    />
  );
};

export default Setup;
